import { css, Global } from '@emotion/react'
import { type FC, type ReactNode, useState } from 'react'

import { borderRadius as borderRadiusTokens } from '@orus.eu/design-tokens/tokens/border-radius-tokens.json'
import { borderStroke as borderStrokeTokens } from '@orus.eu/design-tokens/tokens/border-stroke-tokens.json'
import palette from '@orus.eu/design-tokens/tokens/color-palette.json'
import { colorTokens } from '@orus.eu/design-tokens/tokens/color-tokens.json'
import { shadow as shadowTokens } from '@orus.eu/design-tokens/tokens/shadow-tokens.json'
import { spacing as spacingTokens } from '@orus.eu/design-tokens/tokens/spacing-tokens.json'
import { typescaleTokens } from '@orus.eu/design-tokens/tokens/typescale-tokens.json'
import { TechnicalError } from '@orus.eu/error'

import { ThemeContextProvider, type ThemeName } from './pharaoh-theme-context.ts'

const buildStyles = (theme: ThemeName) => {
  if (!(theme in colorTokens)) {
    throw new TechnicalError(`Unknown Pharaoh Theme: ${theme}`)
  }

  if (!(theme in typescaleTokens)) {
    throw new TechnicalError(`Unknown Pharaoh Theme: ${theme}`)
  }

  if (!(theme in borderRadiusTokens)) {
    throw new TechnicalError(`Unknown Pharaoh Theme: ${theme}`)
  }

  if (!(theme in borderStrokeTokens)) {
    throw new TechnicalError(`Unknown Pharaoh Theme: ${theme}`)
  }

  if (!(theme in spacingTokens)) {
    throw new TechnicalError(`Unknown Pharaoh Theme: ${theme}`)
  }

  if (!(theme in shadowTokens)) {
    throw new TechnicalError(`Unknown Pharaoh Theme: ${theme}`)
  }

  const styles = {
    ...Object.entries(palette).reduce((styles, [key, value]) => ({ ...styles, [`--${key}`]: value }), {}),
    ...Object.entries(colorTokens[theme]).reduce((styles, [key, value]) => ({ ...styles, [`--${key}`]: value }), {}),
    ...Object.entries(typescaleTokens[theme]).reduce(
      (styles, [skinName, value]) => ({
        ...styles,
        ...Object.entries(value).reduce(
          (innerStyles, [variant, innerValue]) => ({
            ...innerStyles,
            [`--${skinName}-${variant}`]: innerValue,
          }),
          {},
        ),
      }),
      {},
    ),
    ...Object.entries(borderRadiusTokens[theme]).reduce(
      (styles, [variant, value]) => ({
        ...styles,
        [`--${variant}`]: value,
      }),
      {},
    ),
    ...Object.entries(borderStrokeTokens[theme]).reduce(
      (styles, [variant, value]) => ({
        ...styles,
        [`--${variant}`]: value,
      }),
      {},
    ),

    ...Object.entries(spacingTokens[theme]).reduce(
      (styles, [variant, value]) => ({
        ...styles,
        [`--${variant}`]: value,
      }),
      {},
    ),

    ...Object.entries(shadowTokens[theme]).reduce(
      (styles, [category, value]) => ({
        ...styles,
        ...Object.entries(value as Record<string, string>).reduce(
          (innerStyles, [variant, innerValue]) => ({
            ...innerStyles,
            [`--shadow-${category}-${variant}`]: innerValue,
          }),
          {},
        ),
      }),
      {},
    ),
  }

  return css({
    ':root': styles,
  })
}

export const PharaohThemeProvider: FC<{
  children: ReactNode
  theme?: ThemeName
}> = ({ theme = 'light', children }) => {
  const [currentTheme, setTheme] = useState<ThemeName | null>(null)
  const styles = buildStyles(currentTheme ?? theme)

  return (
    <ThemeContextProvider value={{ theme: currentTheme, setTheme }}>
      <Global styles={styles} />
      {children}
    </ThemeContextProvider>
  )
}
